import { Component, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ExternalDataService, ScrapCategoryDto } from '../../../services/externalDataService.service';

@Component({
  selector: 'app-create-scrap-item',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './create-scrap-item.component.html',
  styleUrls: ['./create-scrap-item.component.css']
})
export class CreateScrapItemComponent implements OnInit {
  scrapForm: FormGroup;
  @Output() created = new EventEmitter<void>();
  errorMessage = '';
  isSubmitting = false;
  categories: ScrapCategoryDto[] = [];
  isLoadingCategories = false;
  
  alternativeNames: string[] = [];
  newAlternativeName: string = '';

  constructor(private fb: FormBuilder, private externalDataService: ExternalDataService) {
    this.scrapForm = this.fb.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      category: [''],
      categoryId: [null, Validators.required],
      image: [''],
      unit: ['', Validators.required],
      regularPrice: [0, [Validators.required, Validators.min(0)]],
      specialPrice: [0, [Validators.required, Validators.min(0)]],
      dealerPercentage: [0, [Validators.min(0)]],
      isVisibleToCustomer: [true],
      isScrap: [true]
    });
  }

  ngOnInit() {
    this.scrapForm.get('name')?.valueChanges.subscribe(name => {
      if (name) {
        this.scrapForm.patchValue({ image: name + '.png' }, { emitEvent: false });
      }
    });

    // When the user picks a category by id, also sync the category name field
    this.scrapForm.get('categoryId')?.valueChanges.subscribe(selectedId => {
      const match = this.categories.find(c => c.id === selectedId);
      this.scrapForm.patchValue({ category: match?.name ?? '' }, { emitEvent: false });
    });

    this.loadCategories();
  }

  loadCategories() {
    this.isLoadingCategories = true;
    this.externalDataService.getScrapCategories().subscribe({
      next: (categories) => {
        this.categories = categories;
        this.isLoadingCategories = false;
      },
      error: (error) => {
        console.error('Error loading scrap categories:', error);
        this.isLoadingCategories = false;
        // Don't show error message for category loading failure, just log it
      }
    });
  }

  onSubmit() {
    if (this.scrapForm.valid) {
      this.isSubmitting = true;
      this.errorMessage = '';
      
      const { name, description, category, categoryId, unit, regularPrice, specialPrice, dealerPercentage, isVisibleToCustomer, isScrap } = this.scrapForm.value;
      const imageName = name + '.png';
      this.externalDataService.createScrapItem({
        name,
        description,
        category,
        categoryId: categoryId ?? null,
        image: imageName,
        imageName,
        unit,
        regularPrice,
        specialPrice,
        dealerPercentage: dealerPercentage ?? 0,
        isVisibleToCustomer: isVisibleToCustomer ?? true,
        isScrap: isScrap ?? true,
        alternativeNames: this.alternativeNames.length > 0 ? this.alternativeNames : null
      }).subscribe({
        next: (res: any) => {
          // handle success (e.g., show message, reset form)
          this.scrapForm.reset();
          this.alternativeNames = [];
          this.newAlternativeName = '';
          this.isSubmitting = false;
          this.created.emit();
        },
        error: (err: any) => {
          // handle error (e.g., show error message)
          console.error('Error creating scrap item:', err);
          this.isSubmitting = false;
          
          // Extract error message from the response
          if (err?.error?.detail) {
            this.errorMessage = err.error.detail;
          } else if (err?.message) {
            this.errorMessage = err.detail;
          } else if (err?.status === 400) {
            this.errorMessage = 'Invalid data provided. Please check your input.';
          } else if (err?.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (err?.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to create scrap items.';
          } else if (err?.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (err?.status === 0 || err?.status === undefined) {
            this.errorMessage = 'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage = 'Failed to create scrap item. Please try again.';
          }
        }
      });
    }
  }
  
  onClose() {
    this.created.emit();
  }
  
  clearError() {
    this.errorMessage = '';
  }

  addAlternativeName(event?: Event) {
    if (event) {
      event.preventDefault();
    }
    const name = this.newAlternativeName.trim();
    if (name && !this.alternativeNames.includes(name)) {
      this.alternativeNames.push(name);
    }
    this.newAlternativeName = '';
  }

  removeAlternativeName(index: number) {
    this.alternativeNames.splice(index, 1);
  }
}
